import { faMicroscope, faQuestionCircle, faRectanglePro } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import type { JSX } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AvatarV2 as Avatar,
  NavItem,
  NavItemHeader,
  NavItemMinimizable,
  Popover,
} from '@feathr/components';
import NotificationsPopover from '@feathr/extender/components/NotificationsPopover';
import {
  SessionCacheProvider,
  useBlackboxVersion,
  useFeatureLimits,
  useLicense,
} from '@feathr/extender/hooks';
import { useAccount, useFlags, useLocalUrl, useNavigation, useUser } from '@feathr/extender/state';

import CreditsCard from '../CreditsCard';
import CreditsNavItem from '../CreditsNavItem';
import EmailUsageIndicator from '../EmailUsageIndicator';
import HelpCard from '../HelpCard/HelpCard';
import HelpNav from '../HelpNav';
import UserNav from '../UserNav';

import * as styles from './NavBottomSection.css';

interface INavBottomSectionProps {
  className?: string;
  showShadow?: boolean;
}

function NavBottomSection({
  className,
  showShadow = false,
}: Readonly<INavBottomSectionProps>): JSX.Element {
  const { isMinimized } = useNavigation();
  const { t } = useTranslation();
  const localUrl = useLocalUrl();
  const account = useAccount();
  const user = useUser();
  const hasProResources = useLicense('proResourcesPageVisible');
  const { blackboxVersion, isOnProduction } = useBlackboxVersion();
  const { showGrowthCredits } = useFlags();
  const { isEmailLimitsEnabled } = useFeatureLimits();

  const picture = (!!user && user.get('picture')) || '';
  const userName = user.name || user.email;
  const avatarElement = useMemo(
    () => <Avatar image={picture} name={userName} size={'xs'} />,
    [picture, userName],
  );
  const showHelpCard =
    account.isPlatformLite && user.getSetting('show_feathr_lite_help_card', false);
  const showDevelopmentNavUserSetting = user.getSetting('show_dev_nav') ?? false;
  const showDevNavItem =
    !!blackboxVersion &&
    !isOnProduction &&
    (account.isTestAccount || showDevelopmentNavUserSetting);

  const content = (
    <>
      {!isMinimized && (showHelpCard || isEmailLimitsEnabled || showGrowthCredits) && (
        <div className={styles.cards}>
          {showHelpCard && <HelpCard />}
          {isEmailLimitsEnabled && (
            <SessionCacheProvider>
              <EmailUsageIndicator />
            </SessionCacheProvider>
          )}
          {showGrowthCredits && <CreditsCard />}
        </div>
      )}
      {isMinimized && showGrowthCredits && <CreditsNavItem />}
      {showDevNavItem && (
        <NavItemMinimizable
          data-command={'true'}
          data-command-description={t('Development and QA tools')}
          data-command-keywords={'development,qa,testing,debug'}
          data-command-label={t('Development & QA')}
          icon={faMicroscope}
          isMinimized={isMinimized}
          label={t('Development & QA')}
          to={localUrl('/development-qa')}
        />
      )}
      <NavItem
        className={styles.help}
        footer={
          <Popover align={'end'} className={styles.popover} position={'right'} toggle={'onClick'}>
            <NavItemHeader prefix={<FontAwesomeIcon icon={faQuestionCircle} />}>
              {!isMinimized && t('Help')}
            </NavItemHeader>
            <HelpNav />
          </Popover>
        }
        tooltip={isMinimized ? t('Help') : undefined}
        tooltipPosition={'right'}
        wrapTooltip={true}
      />

      {hasProResources && (
        <NavItemMinimizable
          data-command={'true'}
          data-command-description={t('Access professional resources')}
          data-command-keywords={'pro,resources,professional'}
          data-command-label={t('Pro Resources')}
          icon={<FontAwesomeIcon icon={faRectanglePro} />}
          isMinimized={isMinimized}
          label={t('Resources')}
          to={localUrl('/settings/pro-resources')}
        />
      )}

      <NavItem
        className={styles.notifications}
        footer={<NotificationsPopover isMinimized={isMinimized} />}
        tooltip={isMinimized ? t('Notifications') : undefined}
        tooltipPosition={'right'}
        wrapTooltip={true}
      />

      <NavItem
        className={styles.avatar}
        footer={
          <Popover align={'end'} className={styles.popover} position={'right'} toggle={'onClick'}>
            <NavItemHeader prefix={avatarElement}>{!isMinimized && userName}</NavItemHeader>
            <UserNav />
          </Popover>
        }
        key={'user-avatar'}
        tooltip={isMinimized ? userName : undefined}
        tooltipPosition={'right'}
        wrapTooltip={true}
      />
    </>
  );

  return (
    <div
      className={classNames(styles.root, className, {
        [styles.minimized]: isMinimized,
        [styles.showShadow]: showShadow,
      })}
      data-name={'nav-bottom-section'}
    >
      {content}
    </div>
  );
}

export default NavBottomSection;
